import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useState, useEffect, useCallback, createContext, useContext, useRef, lazy, Suspense } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import OneSignal from "react-onesignal";
import { HelmetProvider } from "react-helmet-async";
import ErrorBoundary from "./components/ErrorBoundary";

import { motion, AnimatePresence } from "framer-motion";

// ─── EAGER pages — nel critical path SEO/landing/auth ───
// Queste vengono richieste da bot Googlebot o sono la prima route dopo signup:
// tenerle eager evita un flash + un round-trip aggiuntivo su JS chunk.
import LandingPage from "./pages/LandingPage";
import PropertyLanding from "./pages/PropertyLanding";
import CityLanding from "./pages/CityLanding";
import GuestHome from "./pages/GuestHome";
import PrenotaSenzaCommissioni from "./pages/PrenotaSenzaCommissioni";
import GuestbookApp from "./pages/GuestbookApp";
import GuestbookV3 from "./pages/guestbook-v3/GuestbookV3";
import LiveDemoViewer from "./pages/LiveDemoViewer";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";
import SeoGuestbookLanding from "./pages/SeoGuestbookLanding";
import PluginLanding from "./pages/PluginLanding";
import ParkingLanding from "./pages/ParkingLanding";
import IniziaLanding from "./pages/IniziaLanding";
import ProLanding from "./pages/ProLanding";
import ProRegister from "./pages/ProRegister";
import ProDashboard from "./pages/ProDashboard";
import ProHostSection from "./pages/ProHostSection";
import ProAdmin from "./pages/ProAdmin";
import PropertyPWAAdmin from "./pages/PropertyPWAAdmin";
import DevisuAdmin from "./pages/DevisuAdmin";
import PluginCheckinShowcase from "./pages/PluginCheckinShowcase";

// ─── LAZY pages — split-chunk per un boot iniziale più snello ───
// Ogni pagina è >100kB minified; caricarle on-demand riduce del ~60%
// il JS necessario alla prima interazione dell'utente.
const GuideHub = lazy(() => import("./pages/GuideHub"));
const GuidePage = lazy(() => import("./pages/GuidePage"));
const BlogHub = lazy(() => import("./pages/BlogHub"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const PropertyEditor = lazy(() => import("./pages/PropertyEditor"));
const CheckinDocumenti = lazy(() => import("./pages/CheckinDocumenti"));
const Control = lazy(() => import("./pages/Control"));
const Checkin = lazy(() => import("./pages/Checkin"));
const Burocrazia = lazy(() => import("./pages/Burocrazia"));
const Identifica = lazy(() => import("./pages/Identifica"));
const DevisuGuest = lazy(() => import("./pages/DevisuGuest"));
const DevisuHost = lazy(() => import("./pages/DevisuHost"));
const DevisuDoorLanding = lazy(() => import("./pages/DevisuDoorLanding"));
const PropertyCalendarPage = lazy(() => import("./pages/PropertyCalendarPage"));
const GlobalMap = lazy(() => import("./pages/GlobalMap"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const VieniDaNoi = lazy(() => import("./pages/VieniDaNoi"));
const Profile = lazy(() => import("./pages/Profile"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const GuestDashboard = lazy(() => import("./pages/GuestDashboard"));
const BlogManager = lazy(() => import("./pages/BlogManager"));
const LoyaltyManager = lazy(() => import("./pages/LoyaltyManager"));
const MaterialeSocial = lazy(() => import("./pages/MaterialeSocial"));
const IntegrazioniPorte = lazy(() => import("./pages/IntegrazioniPorte"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const HelpDesk = lazy(() => import("./pages/HelpDesk"));
const GuestbookEditor = lazy(() => import("./pages/GuestbookEditor"));
const GuestbookExtra = lazy(() => import("./pages/GuestbookExtra"));
const EventsManager = lazy(() => import("./pages/EventsManager"));
const ParkHostDashboard = lazy(() => import("./pages/ParkHostDashboard"));
const UpgradePlan = lazy(() => import("./pages/UpgradePlan"));
const ParkingEditor = lazy(() => import("./pages/ParkingEditor"));
const ParkHostEnroll = lazy(() => import("./pages/ParkHostEnroll"));
const HostStats = lazy(() => import("./pages/HostStats"));
const EventiPublic = lazy(() => import("./pages/eventi/EventiPublic"));
const EventiCitta = lazy(() => import("./pages/eventi/EventiCitta"));
const EventiEnti = lazy(() => import("./pages/eventi/EventiEnti"));
import CookieBanner, { hasAnalyticsConsent, hasMarketingConsent } from "./components/CookieBanner";
import Landy from "./components/Landy";
import AdminPWAInstallPromo from "./components/AdminPWAInstallPromo";
import { TrialBanner, SubscriptionPopup, TrialExpiredWall } from "./components/TrialBanner";
import { LanguageProvider } from "./contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Suspense fallback shared da tutte le lazy pages: minimalista + tema-agnostico
// per evitare flash bianchi mentre React scarica il chunk della route.
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-zinc-50" aria-busy="true" aria-label="Caricamento pagina">
    <div className="w-8 h-8 rounded-full border-2 border-zinc-200 border-t-zinc-900 animate-spin" />
  </div>
);


// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function HtmlPageRedirect({ file }) {
  useEffect(() => { window.location.replace(file); }, [file]);
  return null;
}

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const isLoggingIn = useRef(false);

  const registerFcmToken = useCallback(async (authToken) => {
    try {
      // Tag user with their ID for targeted notifications
      if (authToken) {
        try {
          const meRes = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${authToken}` } });
          const u = meRes.data;
          if (u?.user_id) {
            OneSignal.login(u.user_id);
            OneSignal.User.addTags({ user_type: u.user_type || "host", email: u.email || "" });
          }
        } catch {}
      }
      // Push permission prompt REMOVED (2026-02) — the host requested that
      // push permission be requested EXCLUSIVELY as the last step of a
      // successful PWA install (see PWAInstallPromo / AdminPWAInstallPromo).
      // Auto-prompting post-login was showing the "topo dall'alto" that
      // stacked with the property push sheet and cookie banner.
      // (Was: `OneSignal.Slidedown.promptPush();`)
    } catch (e) { /* silent */ }
  }, []);

  // Only runs on mount — checks if existing token in localStorage is still valid
  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = localStorage.getItem("token");
      if (!savedToken) {
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${savedToken}` }
        });
        setUser(response.data);
        setToken(savedToken);
      } catch (error) {
        setUser(null);
        setToken(null);
        localStorage.removeItem("token");
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []); // Only on mount — no token dependency = no race condition

  const login = async (email, password) => {
    isLoggingIn.current = true;
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    try {
      const meRes = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${newToken}` } });
      setUser(meRes.data);
    } catch {
      setUser(userData);
    }
    // Pending ParkHost enrollment (set at signup when email verification is required):
    // as soon as the user completes the first login post-verification, promote to ParkHost.
    try {
      if (localStorage.getItem("hostland_pending_parkhost_enroll") === "1") {
        await axios.post(`${API}/parkhost/enroll`, {}, { headers: { Authorization: `Bearer ${newToken}` } });
        localStorage.removeItem("hostland_pending_parkhost_enroll");
        try {
          const meRes2 = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${newToken}` } });
          setUser(meRes2.data);
        } catch { /* silent */ }
      }
    } catch { /* silent — user can enroll manually from dashboard */ }
    isLoggingIn.current = false;
    // Register FCM token after login (notifications only after login)
    registerFcmToken(newToken);
    return userData;
  };

  const register = async (email, password, name, userType = "host", discountCode = null, planType = null, billingPreference = null, termsAccepted = false, termsVersion = null) => {
    isLoggingIn.current = true;
    const payload = { email, password, name, user_type: userType, terms_accepted: !!termsAccepted };
    if (termsVersion) payload.terms_version = termsVersion;
    if (discountCode) payload.discount_code = discountCode;
    if (planType) payload.plan_type = planType;
    if (billingPreference) payload.billing_preference = billingPreference;
    const response = await axios.post(`${API}/auth/register`, payload);
    const { token: newToken, user: userData, needs_verification, vdn_grant } = response.data;

    if (needs_verification) {
      isLoggingIn.current = false;
      return { needs_verification: true, user: userData, vdn_grant };
    }

    localStorage.setItem("token", newToken);
    setToken(newToken);
    try {
      const meRes = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${newToken}` } });
      setUser(meRes.data);
    } catch {
      setUser(userData);
    }
    isLoggingIn.current = false;
    registerFcmToken(newToken);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {});
    } catch (e) {
      // ignore
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    setUser,
    setToken,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="glass-card p-8">
          <div className="animate-spin w-8 h-8 border-2 border-zinc-900 border-t-transparent rounded-full mx-auto" />
          <p className="text-zinc-500 mt-4 font-medium">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    const p = location.pathname;
    if (p.startsWith("/control")) {
      const nxt = encodeURIComponent(p + (location.search || ""));
      return <Navigate to={`/control/login?next=${nxt}`} replace />;
    }
    if (p.startsWith("/dashboard")) {
      const nxt = encodeURIComponent(p + (location.search || ""));
      return <Navigate to={`/dashboard/login?next=${nxt}`} replace />;
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Dashboard Router — Guest vs Host
const DashboardRouter = () => {
  const { user } = useAuth();
  if (user?.user_type === "guest") {
    return <GuestDashboard />;
  }
  return <Dashboard />;
};

// Short Link Redirect Component — /s/:slug is now rendered in place by
// PropertyLanding (the landing API resolves slugs), so the URL Google indexes
// is the same one that carries the content and the canonical.

const GuestbookShortLinkRedirect = () => {
  const { slug } = require("react-router-dom").useParams();
  const nav = useNavigate();
  // Il GuestBook è privato per gli ospiti: iniettiamo IMMEDIATAMENTE il meta
  // noindex/nofollow anche sulla pagina di redirect così se un crawler dovesse
  // seguire un link /g/{slug} non indicizzerebbe mai il contenuto intermedio.
  // Un slug invalido rimanda a /404 (che è già escluso dai motori) e NON alla
  // homepage indicizzabile — questo evita di generare backlink accidentali.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive, nosnippet";
    meta.setAttribute("data-guestbook-shortlink-noindex", "1");
    document.head.appendChild(meta);
    return () => { try { document.head.removeChild(meta); } catch { /* già rimosso */ } };
  }, []);
  useEffect(() => {
    axios.get(`${API}/g/${slug}`).then(r => {
      nav(`/guestbook/${r.data.property_id}${window.location.search}`, { replace: true });
    }).catch(() => nav("/404", { replace: true }));
  }, [slug, nav]);
  return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" /></div>;
};

// App Router
// ═══ SPLASH SCREEN — Cody waving ═══
const SplashCody = () => (
  <svg width="160" height="180" viewBox="-20 -14 88 90" style={{ overflow: "visible" }}>
    {/* Legs */}
    <line x1="12" y1="42" x2="10" y2="54" stroke="#18181b" strokeWidth="4" strokeLinecap="round" />
    <line x1="24" y1="42" x2="26" y2="54" stroke="#18181b" strokeWidth="4" strokeLinecap="round" />
    <ellipse cx="10" cy="55" rx="4" ry="3" fill="#18181b" />
    <ellipse cx="26" cy="55" rx="4" ry="3" fill="#18181b" />
    {/* Waving arm */}
    <motion.g animate={{ rotate: [-15, -60, -15, -60, -15] }} transition={{ duration: 0.5, repeat: Infinity, ease: "easeInOut" }} style={{ transformOrigin: "4px 22px" }}>
      <line x1="3" y1="22" x2="-10" y2="8" stroke="#18181b" strokeWidth="4" strokeLinecap="round" />
      <g transform="translate(-11, 3)">
        <circle cx="0" cy="0" r="5" fill="#18181b" />
        <motion.g animate={{ rotate: [-12, 12, -12] }} transition={{ duration: 0.25, repeat: Infinity }}>
          <line x1="-4" y1="-2" x2="-8" y2="-8" stroke="#18181b" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="0" y1="-4" x2="0" y2="-10" stroke="#18181b" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="4" y1="-2" x2="8" y2="-8" stroke="#18181b" strokeWidth="2.5" strokeLinecap="round" />
        </motion.g>
      </g>
    </motion.g>
    {/* Right arm holding a stylized trolley/suitcase */}
    <line x1="33" y1="22" x2="42" y2="36" stroke="#18181b" strokeWidth="4" strokeLinecap="round" />
    <circle cx="43" cy="37" r="4" fill="#18181b" />
    {/* Trolley — telescopic handle + suitcase + wheels, suggests "travel" without overwhelming the mascot */}
    <g>
      {/* Telescopic handle (T-shape) coming out of the hand */}
      <line x1="43" y1="36" x2="43" y2="28" stroke="#18181b" strokeWidth="2.4" strokeLinecap="round" />
      <line x1="40" y1="28" x2="50" y2="28" stroke="#18181b" strokeWidth="2.4" strokeLinecap="round" />
      {/* Suitcase body */}
      <rect x="40" y="40" width="13" height="16" rx="2.2" fill="white" stroke="#18181b" strokeWidth="2.6" />
      {/* Horizontal strap */}
      <line x1="40" y1="47" x2="53" y2="47" stroke="#18181b" strokeWidth="1.6" />
      {/* Brand square detail */}
      <rect x="42" y="42" width="3" height="3" rx="0.6" fill="#FF385C" />
      {/* Wheels */}
      <circle cx="43" cy="58" r="1.8" fill="#18181b" />
      <circle cx="50" cy="58" r="1.8" fill="#18181b" />
    </g>
    {/* QR Body */}
    <rect x="0" y="4" width="36" height="36" rx="6" fill="white" stroke="#18181b" strokeWidth="3" />
    <rect x="4" y="8" width="10" height="10" rx="2" fill="#18181b" />
    <rect x="5.8" y="9.8" width="6.4" height="6.4" rx="1" fill="white" />
    <rect x="7.5" y="11.5" width="3" height="3" fill="#18181b" />
    <rect x="22" y="8" width="10" height="10" rx="2" fill="#18181b" />
    <rect x="23.8" y="9.8" width="6.4" height="6.4" rx="1" fill="white" />
    <rect x="25.5" y="11.5" width="3" height="3" fill="#18181b" />
    <rect x="4" y="26" width="10" height="10" rx="2" fill="#18181b" />
    <rect x="5.8" y="27.8" width="6.4" height="6.4" rx="1" fill="white" />
    <rect x="7.5" y="29.5" width="3" height="3" fill="#18181b" />
    <rect x="17" y="8" width="3" height="3" fill="#18181b" />
    <rect x="4" y="21" width="3" height="3" fill="#18181b" />
    <rect x="29" y="21" width="3" height="3" fill="#18181b" />
    <rect x="21" y="32" width="3" height="3" fill="#18181b" />
    <rect x="17" y="21" width="3" height="3" fill="#18181b" />
    {/* Open eyes — with subtle blink + tiny highlight to feel alive */}
    <motion.g
      animate={{ scaleY: [1, 1, 0.1, 1, 1, 1, 0.1, 1] }}
      transition={{ duration: 4, repeat: Infinity, times: [0, 0.45, 0.48, 0.51, 0.7, 0.93, 0.96, 1], ease: "easeInOut" }}
      style={{ transformOrigin: "12px 21px" }}
    >
      <circle cx="12" cy="21" r="2.8" fill="#18181b" />
      <circle cx="13" cy="20" r="0.9" fill="white" />
    </motion.g>
    <motion.g
      animate={{ scaleY: [1, 1, 0.1, 1, 1, 1, 0.1, 1] }}
      transition={{ duration: 4, repeat: Infinity, times: [0, 0.45, 0.48, 0.51, 0.7, 0.93, 0.96, 1], ease: "easeInOut" }}
      style={{ transformOrigin: "24px 21px" }}
    >
      <circle cx="24" cy="21" r="2.8" fill="#18181b" />
      <circle cx="25" cy="20" r="0.9" fill="white" />
    </motion.g>
    {/* Wide smiley mouth — bigger curve than before to feel genuinely happy */}
    <path d="M 8 28 Q 18 39 28 28" stroke="#18181b" strokeWidth="2.6" strokeLinecap="round" fill="none" />
    {/* Cheek blush */}
    <ellipse cx="7" cy="27" rx="4" ry="2.5" fill="#FFB6C1" opacity="0.6" />
    <ellipse cx="29" cy="27" rx="4" ry="2.5" fill="#FFB6C1" opacity="0.6" />
  </svg>
);

const SplashTypewriter = ({ text, delay = 0 }) => {
  const [displayed, setDisplayed] = useState("");
  useEffect(() => {
    const chars = text.split("");
    const timers = chars.map((_, i) =>
      setTimeout(() => setDisplayed(text.slice(0, i + 1)), delay + i * 55)
    );
    return () => timers.forEach(clearTimeout);
  }, [text, delay]);
  return (
    <span>
      {displayed}
      {displayed.length < text.length && (
        <span className="inline-block w-[3px] h-[0.85em] bg-zinc-900 ml-1 align-middle animate-pulse" />
      )}
    </span>
  );
};

const SplashScreen = ({ onFinish }) => {
  const navigate = useNavigate();

  const handleChoice = (type) => {
    try { localStorage.setItem("hostland_user_type", type); } catch {}
    onFinish();
    // small delay to allow exit animation
    setTimeout(() => {
      if (type === "guest") navigate("/prenotasenzacommissioni", { replace: true });
      // host stays on / (current)
    }, 60);
  };

  return (
    <motion.div
      exit={{ opacity: 0, scale: 1.05 }}
      transition={{ duration: 0.45, ease: "easeInOut" }}
      className="fixed inset-0 z-[9999] bg-gradient-to-br from-white via-white to-zinc-50 flex flex-col items-center justify-center px-6"
    >
      <motion.div
        initial={{ y: 60, opacity: 0, scale: 0.5 }}
        animate={{ y: [60, -10, 0], opacity: 1, scale: [0.5, 1.08, 1] }}
        transition={{ duration: 0.7, ease: "easeOut" }}
      >
        <SplashCody />
      </motion.div>

      {/* p (non h1): l'h1 della homepage è quello della LandingPage — due h1
          sulla stessa pagina confondono i motori di ricerca. */}
      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.45 }}
        className="text-3xl sm:text-4xl lg:text-5xl font-black text-zinc-900 tracking-tighter mt-6 text-center"
      >
        Welcome to <span style={{ color: "#FF385C" }}>Hostland</span>
      </motion.p>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9, duration: 0.45 }}
        className="text-sm md:text-base text-zinc-500 font-medium mt-3 mb-8 text-center"
      >
        Per iniziare, dicci chi sei
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1, duration: 0.45 }}
        className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-md"
      >
        <button
          onClick={() => handleChoice("guest")}
          className="group relative overflow-hidden bg-white border-2 border-zinc-200 hover:border-zinc-900 hover:shadow-xl rounded-3xl p-5 text-left transition-all"
          data-testid="welcome-gate-guest"
        >
          <div className="text-3xl mb-2">🌍</div>
          <div className="font-black text-zinc-900 text-base tracking-tight">Sono un viaggiatore</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">Cerco la mia prossima vacanza</div>
        </button>
        <button
          onClick={() => handleChoice("host")}
          className="group relative overflow-hidden bg-zinc-900 text-white rounded-3xl p-5 text-left hover:shadow-2xl hover:scale-[1.02] transition-all"
          data-testid="welcome-gate-host"
        >
          <div className="text-3xl mb-2">🏡</div>
          <div className="font-black text-base tracking-tight">Sono un Host o PM</div>
          <div className="text-[11px] text-white/60 mt-0.5">Gestisco una o più strutture</div>
        </button>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.6 }}
        transition={{ delay: 1.6, duration: 0.5 }}
        className="text-xs text-zinc-400 mt-8 text-center"
      >
        Lo spazio dove Host e ospiti si incontrano
      </motion.p>
    </motion.div>
  );
};

/**
 * PWALaunchRedirect — safety-net for the Safari iOS/macOS case where an
 * installed WebApp launches at the ORIGIN ROOT (`/`) instead of honoring
 * `manifest.start_url`. When the shortcut boots, we check localStorage
 * for the last stashed `hostland_pwa_launch_target` (written by
 * PWAInstallPromo right before firing the install) and redirect the
 * guest there. Only fires ONCE per install to avoid loops.
 */
const PWALaunchRedirect = () => {
  useEffect(() => {
    try {
      const isStandalone = window.matchMedia?.("(display-mode: standalone)").matches
        || window.navigator?.standalone === true;
      if (!isStandalone) return;
      if (window.location.pathname !== "/") return; // only rescue root launches
      const raw = localStorage.getItem("hostland_pwa_launch_target");
      if (!raw) return;
      const target = JSON.parse(raw);
      if (!target?.url || target.url === "/") return;
      // Guard against replay loops — only redirect once per install
      const redirected = localStorage.getItem("hostland_pwa_launch_target_used");
      if (redirected === target.url) return;
      localStorage.setItem("hostland_pwa_launch_target_used", target.url);
      // Use replace so back button doesn't loop back to root
      window.location.replace(target.url);
    } catch { /* private mode / bad JSON — no-op */ }
  }, []);
  return null;
};

/**
 * AdminPWAScopedMount — renders the AdminPWAInstallPromo only when the
 * user is on a `/dashboard*` route AND authenticated. Also swaps every
 * PWA-related tag in <head> so Safari/Chrome install "Hostland Admin"
 * with scope `/dashboard` — never the public homepage.
 *
 * We remove-and-reinsert the manifest link (instead of just changing its
 * `href`) because iOS Safari + macOS Safari cache the parsed manifest on
 * first read; changing the attribute alone leaves the OLD manifest active.
 * The remove-then-add trick forces a fresh parse right before install.
 *
 * We also override `apple-mobile-web-app-title` so the installed app icon
 * on iOS Home Screen reads "Hostland" instead of "Vita da Host".
 */
const AdminPWAScopedMount = () => {
  const { user } = useAuth();
  const location = useLocation();
  const onDashboard = location.pathname === "/dashboard"
    || location.pathname.startsWith("/dashboard/");

  useEffect(() => {
    if (!onDashboard) return;
    const head = document.head;
    if (!head) return;

    // 1) Manifest link — remove existing (any origin), inject a fresh one
    //    built as a `data:` URL. Same rationale as PropertyLanding: Safari
    //    caches network-fetched manifests aggressively and often shows the
    //    default Hostland install prompt before our React swap kicks in.
    //    An inline manifest via data-URL is read synchronously → no race,
    //    no cache, no wrong metadata in "Aggiungi al Dock".
    const oldLinks = Array.from(document.querySelectorAll('link[rel="manifest"]'));
    oldLinks.forEach((l) => l.parentNode.removeChild(l));

    // PWA /dashboard DISATTIVATA: non iniettiamo alcun manifest su /dashboard,
    // così l'area admin NON è più installabile come app. L'unica web-app host
    // installabile è Hostland Control (/control → manifest-control.json).
    // Rimuoviamo solo i manifest esistenti (ripristinati all'uscita dalla rotta).

    // 2) Apple-specific meta tags — iOS Safari uses these instead of the manifest
    //    to derive the installed-app name, colour and status-bar style.
    const setMeta = (name, content) => {
      let m = document.querySelector(`meta[name="${name}"]`);
      if (!m) { m = document.createElement("meta"); m.name = name; head.appendChild(m); }
      const prev = m.getAttribute("content");
      m.setAttribute("content", content);
      return { m, prev };
    };
    const restoreMeta = (o) => { if (o?.prev != null) o.m.setAttribute("content", o.prev); };
    const patches = [
      setMeta("apple-mobile-web-app-title", "Hostland"),
      setMeta("apple-mobile-web-app-status-bar-style", "black-translucent"),
      setMeta("theme-color", "#d04260"),
    ];

    return () => {
      // Restore the public manifest / meta tags when the user leaves /dashboard
      oldLinks.forEach((l) => head.appendChild(l));
      patches.forEach(restoreMeta);
    };
  }, [onDashboard]);

  if (!onDashboard || !user) return null;
  // Popup PWA "installa la webapp" rimosso su richiesta (inutile / crea disordine).
  // Manteniamo solo lo scoping del manifest admin (effetto sopra), senza banner.
  return null;
};

const AppRouter = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [showSubPopup, setShowSubPopup] = useState(false);

  // OneSignal foreground notifications are handled by the SDK automatically
  useEffect(() => {}, []);

  // Google Analytics 4 — SPA route tracking. The initial page_view fires from
  // the gtag snippet in public/index.html; this effect records every subsequent
  // client-side navigation so the dashboard shows real per-page pageviews.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    if (!hasAnalyticsConsent()) return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);

  // Meta Pixel — SPA route tracking. La PageView iniziale parte dallo snippet in
  // public/index.html; questo effect traccia ogni navigazione client-side
  // SUCCESSIVA. Salta il primo mount così non duplichiamo la PageView iniziale.
  const fbqFirstRef = useRef(true);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.fbq !== "function") return;
    if (!hasMarketingConsent()) return;
    if (fbqFirstRef.current) { fbqFirstRef.current = false; return; }
    window.fbq("track", "PageView");
  }, [location.pathname, location.search]);

  // Prerender readiness: window.prerenderReady stays false while any page still
  // renders a [data-prerender-pending] loading marker (data not yet fetched).
  // SAFETY TIMEOUT: after 7s the signal is forced to true no matter what, so a
  // slow/failed API call can never leave the crawler stuck on `false` (which
  // would serve Googlebot an incomplete snapshot / loading splash).
  useEffect(() => {
    let raf = 0;
    let forced = false;
    const update = () => {
      raf = 0;
      if (forced) { window.prerenderReady = true; return; }
      window.prerenderReady = !document.querySelector("[data-prerender-pending]");
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(update); };
    schedule();
    const obs = new MutationObserver(schedule);
    obs.observe(document.body, { childList: true, subtree: true });
    const safety = setTimeout(() => { forced = true; window.prerenderReady = true; }, 7000);
    return () => { obs.disconnect(); clearTimeout(safety); if (raf) cancelAnimationFrame(raf); };
  }, []);

  // Show subscription popup when trial expires (NOT for guests)
  useEffect(() => {
    if (user?.trial_info?.trial_expired && user?.trial_info?.must_subscribe && user?.user_type !== "guest") {
      const popupDismissed = sessionStorage.getItem("sub_popup_dismissed");
      if (!popupDismissed) {
        setShowSubPopup(true);
      }
    }
  }, [user]);

  const handleSelectPlan = async (plan) => {
    // For subscriptions, the TrialExpiredWall/SubscriptionPopup handle payment inline with Stripe Elements.
    // This is fallback for one-time payments (QR, creation fee)
    setShowSubPopup(false);
    if (plan === "monthly" || plan === "yearly") return; // Handled inline
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        package_type: plan,
        origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error("Errore nel pagamento. Riprova.");
    }
  };

  const trialInfo = user?.trial_info;
  // Pages where the trial banner must NEVER appear (public-facing surfaces shown to guests
  // and SEO crawlers — they shouldn't see internal host-only nags).
  const isPublicPage =
    ["/", "/login", "/classifica", "/search", "/privacy", "/terms", "/cookie-policy", "/viaggiatori", "/prenotasenzacommissioni", "/inizia"].includes(location.pathname)
    || location.pathname.startsWith("/pro")            // landing pubblica Aziende + area Pro (non è il trial host)
    || location.pathname.startsWith("/eventi")         // Hostland Eventi: pagine pubbliche
    || location.pathname.startsWith("/property/")
    || location.pathname.startsWith("/s/")
    || location.pathname.startsWith("/g/")
    || location.pathname.startsWith("/guestbook/")     // public GuestBook accessed via QR
    || location.pathname.startsWith("/guestbook-")     // SEO landings: /guestbook-affitti-brevi, /guestbook-hotel, …
    || location.pathname.startsWith("/welcome-book-")
    || location.pathname.startsWith("/manuale-casa-")
    || location.pathname.startsWith("/case-vacanze/")
    || location.pathname.startsWith("/alloggi-senza-commissioni/")
    || location.pathname.startsWith("/guide");
  const showBanner = user && trialInfo && (trialInfo.is_trial || trialInfo.trial_expired) && user.role !== "admin" && user.user_type !== "guest" && !isPublicPage;
  const showExpiredWall = user && trialInfo?.trial_expired && trialInfo?.must_subscribe && user.role !== "admin" && user.user_type !== "guest" && !isPublicPage;

  return (
    <>
      {showExpiredWall && (
        <TrialExpiredWall
          trialInfo={trialInfo}
          onSelectPlan={handleSelectPlan}
          onLogout={logout}
          token={token}
        />
      )}
      {showBanner && !showExpiredWall && (
        <TrialBanner
          trialInfo={trialInfo}
          onSubscribe={() => setShowSubPopup(true)}
        />
      )}
      <SubscriptionPopup
        isOpen={showSubPopup}
        onClose={() => {
          setShowSubPopup(false);
          sessionStorage.setItem("sub_popup_dismissed", "1");
        }}
        onSelectPlan={handleSelectPlan}
        token={token}
      />
      <Suspense fallback={<PageLoader />}>
      <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/inizia" element={<IniziaLanding />} />
      <Route path="/pro" element={<ProLanding />} />
      <Route path="/pro/register" element={<ProRegister />} />
      <Route path="/pro/dashboard" element={<ProDashboard />} />
      <Route path="/pro/host/:propertyId" element={<ProHostSection />} />
      <Route path="/pro/admin" element={<ProAdmin />} />
      <Route path="/dashboard/property/:propertyId/pwa" element={<PropertyPWAAdmin />} />
      <Route path="/dashboard/devisu" element={<ProtectedRoute><DevisuAdmin /></ProtectedRoute>} />
      <Route path="/dashboard/plugin-checkin" element={<ProtectedRoute><PluginCheckinShowcase /></ProtectedRoute>} />
      <Route path="/login" element={<Login />} />
      <Route path="/hostlander" element={<HtmlPageRedirect file="/hostlander.html" />} />
      <Route path="/hostlander/" element={<HtmlPageRedirect file="/hostlander.html" />} />
      <Route path="/Hostlander" element={<HtmlPageRedirect file="/hostlander.html" />} />
      <Route path="/control/login" element={<Login />} />
      <Route path="/dashboard/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/vienidanoi" element={<VieniDaNoi />} />
      <Route path="/property/:propertyId" element={<PropertyLanding />} />
      <Route path="/case-vacanze/:slug" element={<CityLanding />} />
      <Route path="/alloggi-senza-commissioni/:slug" element={<CityLanding />} />
      <Route path="/guide" element={<GuideHub />} />
      <Route path="/guide/:slug" element={<GuidePage />} />
      <Route path="/blog" element={<BlogHub />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/viaggiatori" element={<Navigate to="/prenotasenzacommissioni" replace />} />
      <Route path="/prenotasenzacommissioni" element={<PrenotaSenzaCommissioni />} />
      <Route path="/map" element={<GlobalMap />} />
      <Route path="/search" element={<SearchResults />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/eventi" element={<EventiPublic />} />
      <Route path="/eventi/enti" element={<EventiEnti />} />
      <Route path="/eventi/:slug" element={<EventiCitta />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/property/new" 
        element={
          <ProtectedRoute>
            <PropertyEditor />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/dashboard/upgrade"
        element={
          <ProtectedRoute>
            <UpgradePlan />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/dashboard/property/:propertyId" 
        element={
          <ProtectedRoute>
            <PropertyEditor />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/property/:propertyId/guestbook" 
        element={
          <ProtectedRoute>
            <GuestbookEditor />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/dashboard/property/:propertyId/eventi"
        element={
          <ProtectedRoute>
            <EventsManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/property/:propertyId/voci"
        element={
          <ProtectedRoute>
            <GuestbookExtra section="voci" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/property/:propertyId/contatti"
        element={
          <ProtectedRoute>
            <GuestbookExtra section="contatti" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/property/:propertyId/checkin-documenti"
        element={
          <ProtectedRoute>
            <CheckinDocumenti />
          </ProtectedRoute>
        }
      />
      {/* ─── ParkHost routes ─── */}
      <Route path="/parkhost/enroll" element={<ProtectedRoute><ParkHostEnroll /></ProtectedRoute>} />
      <Route path="/dashboard/park" element={<ProtectedRoute><ParkHostDashboard /></ProtectedRoute>} />
      <Route path="/dashboard/park/new" element={<ProtectedRoute><ParkingEditor /></ProtectedRoute>} />
      <Route path="/dashboard/park/:parkingId" element={<ProtectedRoute><ParkingEditor /></ProtectedRoute>} />
      <Route path="/parcheggio/:parkingId" element={<ParkingLanding />} />
      <Route path="/guestbook/:propertyId" element={<GuestbookV3 />} />
      <Route path="/guestbook-v2/:propertyId" element={<GuestbookApp />} />
      <Route path="/guestbook-v3/:propertyId" element={<GuestbookV3 />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route 
        path="/dashboard/profile" 
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } 
      />
      <Route path="/s/:slug" element={<PropertyLanding />} />
      <Route path="/control" element={<ProtectedRoute><Control /></ProtectedRoute>} />
      <Route path="/control/devisu/:sessionId" element={<DevisuHost />} />
      <Route path="/devisu/id/:stayToken" element={<DevisuGuest />} />
      <Route path="/dv/:doorToken" element={<DevisuDoorLanding />} />
      <Route path="/identifica/:token" element={<Identifica />} />
      <Route path="/checkin/:token" element={<Checkin />} />
      <Route path="/burocrazia/:token" element={<Burocrazia />} />
      <Route path="/g/:slug" element={<GuestbookShortLinkRedirect />} />
      <Route path="/live/:kind" element={<LiveDemoViewer />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route
        path="/dashboard/help"
        element={
          <ProtectedRoute>
            <HelpDesk />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/stats"
        element={
          <ProtectedRoute>
            <HostStats />
          </ProtectedRoute>
        }
      />
      <Route path="/my" element={<GuestDashboard />} />
      <Route
        path="/dashboard/property/:propertyId/blog"
        element={
          <ProtectedRoute>
            <BlogManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/property/:propertyId/loyalty"
        element={
          <ProtectedRoute>
            <LoyaltyManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/property/:propertyId/materiale-social"
        element={
          <ProtectedRoute>
            <MaterialeSocial />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/property/:propertyId/integrazioni-porte"
        element={
          <ProtectedRoute>
            <IntegrazioniPorte />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/property/:propertyId/calendar"
        element={
          <ProtectedRoute>
            <PropertyCalendarPage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        } 
      />
      {/* ═══ SEO Mega-Landing GuestBook (indicizzate Google) ═══ */}
      <Route path="/plugin/:slug" element={<PluginLanding />} />
      <Route path="/guestbook-digitale" element={<SeoGuestbookLanding />} />
      <Route path="/guestbook-affitti-brevi" element={<SeoGuestbookLanding />} />
      <Route path="/guestbook-hotel" element={<SeoGuestbookLanding />} />
      <Route path="/guestbook-boutique-hotel" element={<SeoGuestbookLanding />} />
      <Route path="/guestbook-bnb" element={<SeoGuestbookLanding />} />
      <Route path="/guestbook-affittacamere" element={<SeoGuestbookLanding />} />
      <Route path="/guestbook-casa-vacanza" element={<SeoGuestbookLanding />} />
      <Route path="/guestbook-agriturismo" element={<SeoGuestbookLanding />} />
      <Route path="/guestbook-residence" element={<SeoGuestbookLanding />} />
      <Route path="/welcome-book-digitale" element={<SeoGuestbookLanding />} />
      <Route path="/manuale-casa-digitale" element={<SeoGuestbookLanding />} />
      <Route path="/guestbook-guesthouse" element={<SeoGuestbookLanding />} />
      <Route path="/guestbook-glamping" element={<SeoGuestbookLanding />} />
      <Route path="/sito-web-per-host" element={<SeoGuestbookLanding />} />
      <Route path="/crea-sito-casa-vacanza" element={<SeoGuestbookLanding />} />
      <Route path="/sito-web-bb" element={<SeoGuestbookLanding />} />
      <Route path="/sito-web-boutique-hotel" element={<SeoGuestbookLanding />} />
      <Route path="/sito-web-affittacamere" element={<SeoGuestbookLanding />} />
      <Route path="/sito-web-guesthouse" element={<SeoGuestbookLanding />} />
      <Route path="/sito-web-agriturismo" element={<SeoGuestbookLanding />} />
      <Route path="/sito-web-glamping" element={<SeoGuestbookLanding />} />
      <Route path="/sito-web-property-manager" element={<SeoGuestbookLanding />} />
      <Route path="/prenotazioni-dirette-senza-commissioni" element={<SeoGuestbookLanding />} />
      <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </>
  );
};

function App() {
  // Splash gate DISABILITATO (Feb 2026) — per volere dell'utente ora si atterra
  // direttamente sulla homepage unificata (LandingPage) che gestisce internamente
  // il toggle Host/Viaggiatore. Zero interstitial → migliore Core Web Vitals e
  // migliore indicizzazione Google (nessun overlay che copre il contenuto).
  const [showSplash] = useState(false);

  // Initialize OneSignal AFTER user gives marketing consent (GDPR consent-first).
  // Without consent, OneSignal SDK is never loaded → no cookies / fingerprinting.
  //
  // Also skip when we're not on the production origin — OneSignal is scoped
  // to hostland.vitadahost.it so trying to init on preview/staging throws
  // "Can only be used on: …". Skipping keeps the console clean and the
  // ErrorBoundary from mistakenly reacting to third-party init errors.
  useEffect(() => {
    let initialized = false;
    const isProdOrigin = typeof window !== "undefined"
      && /(^|\.)hostland\.vitadahost\.it$/.test(window.location.hostname);
    if (!isProdOrigin) return; // no-op on preview / localhost
    const initOneSignal = async () => {
      if (initialized) return;
      initialized = true;
      try {
        await OneSignal.init({
          appId: process.env.REACT_APP_ONESIGNAL_APP_ID,
          allowLocalhostAsSecureOrigin: true,
          serviceWorkerPath: "/OneSignalSDKWorker.js",
          // All auto-prompt surfaces DISABLED — per host decision the ONLY
          // moment we ask for push permission is right after a successful
          // PWA install (see PWAInstallPromo.doInstall / installedHandler).
          //   • autoRegister:   don't silently register a subscription
          //   • autoResubscribe: don't reprompt previously-permitted users
          //   • notifyButton:    hide the OneSignal bell widget
          //   • slidedown:       no in-page prompt cards
          autoRegister: false,
          autoResubscribe: false,
          notifyButton: { enable: false },
          promptOptions: { slidedown: { prompts: [] } },
        });
      } catch {}
    };

    // Check saved consent on mount
    const hasMarketingSaved = (() => {
      try {
        const raw = localStorage.getItem("cookie_consent");
        if (!raw) return false;
        if (raw === "all") return true;
        if (raw === "essential") return false;
        const parsed = JSON.parse(raw);
        return !!(parsed && parsed.marketing);
      } catch { return false; }
    })();
    if (hasMarketingSaved) {
      initOneSignal();
    }
    // Listen for live consent updates from CookieBanner (granular categories)
    const onConsent = (e) => {
      if (e?.detail?.categories?.marketing || e?.detail?.level === "all") initOneSignal();
    };
    window.addEventListener("hostland-consent", onConsent);
    return () => window.removeEventListener("hostland-consent", onConsent);
  }, []);

  const handleSplashDone = useCallback(() => {
    // Splash disabilitata — funzione lasciata come no-op per compatibilità del render tree.
  }, []);

  return (
    <ErrorBoundary>
    <HelmetProvider>
    {/* ATTENZIONE SEO: NON aggiungere un <Helmet> globale qui e NON hardcodare
        <title>/<meta description> in index.html. Con React 19 react-helmet-async
        renderizza tag DOM reali SENZA dedup → si creerebbero 2 <title> per pagina
        (causa del "Scansionata ma non indicizzata" in GSC). Ogni route pubblica
        deve avere il PROPRIO <Helmet> (o gestire document.title direttamente). */}
    <LanguageProvider>
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors />
        {/* AppRouter è SEMPRE montato: così Googlebot che esegue JS vede il
            contenuto completo della LandingPage (H1, hero, mappa, FAQ) anche
            sulla home /. Il Splash Gate resta come overlay finché l'utente
            non sceglie viaggiatore/host — questa scelta è puramente UX e non
            impatta l'HTML servito ai crawler.
            IMPORTANTE per SEO: senza questo la home appariva come "thin content"
            perché il Router era wrapped in `{!showSplash && …}` e Google non
            vedeva mai il markup dettagliato della LandingPage. */}
        <AppRouter />
        <PWALaunchRedirect />
        <CookieBanner />
        <Landy />
        <AdminPWAScopedMount />
        <AnimatePresence>
          {showSplash && <SplashScreen onFinish={handleSplashDone} />}
        </AnimatePresence>
      </AuthProvider>
    </BrowserRouter>
    </LanguageProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
export { API, BACKEND_URL };
